import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Button,
  Text,
  useDisclosure,
  Accordion,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';

import { useMutation } from '@tanstack/react-query';

import ViewGoogleMapsModal from '../components/ViewGoogleMapsModal';
import CollapsibleItemCard from '../components/CollapsibleItemCard';
import AddItemButton from '../components/AddItemButton';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import {
  VehicleCard,
  VehicleGaraging,
  VehicleUseDistanceValue,
  VehiclePurchaseInfo,
  VehicleCoverages,
} from './vehicle';
import type { Vehicle, VehiclesData } from 'api';
import {
  apiPostalCodeLookup,
  type VinLookupResult,
} from 'api/quote-marketplace';
import { createEmptyVehicle } from '../constants/vehicleOptions';
import {
  useMarketplaceToast,
  useAnswerCodes,
  useLockAwareAccordion,
  ValidationEntityProvider,
} from '../hooks';

interface VehicleStepProps {
  data: VehiclesData;
  quoteId: number;
  onUpdate: (vehicles: Vehicle[]) => void;
  isUpdating?: boolean;
}

const VehicleStep = ({
  data,
  quoteId,
  onUpdate,
  isUpdating,
}: VehicleStepProps) => {
  const toast = useMarketplaceToast();
  const { getLabel, getCode } = useAnswerCodes();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isVinErrorOpen,
    onOpen: onVinErrorOpen,
    onClose: onVinErrorClose,
  } = useDisclosure();
  const {
    isOpen: isMapOpen,
    onOpen: onMapOpen,
    onClose: onMapClose,
  } = useDisclosure();
  const [mapVehicleIndex, setMapVehicleIndex] = useState<number | null>(null);
  const [vehicleToDelete, setVehicleToDelete] = useState<number | null>(null);
  const [vinErrorMessage, setVinErrorMessage] = useState<string>('');
  const [vinErrorVin, setVinErrorVin] = useState<string>('');
  const {
    isOpen: isOwnerTypeOpen,
    onOpen: onOwnerTypeOpen,
    onClose: onOwnerTypeClose,
  } = useDisclosure();
  const [ownerTypeModalIndex, setOwnerTypeModalIndex] = useState<number | null>(
    null,
  );

  const [vehicles, setVehicles] = useState<Vehicle[]>(data.vehicles || []);

  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const accordionProps = useLockAwareAccordion({
    itemCount: vehicles.length,
    expandedIndex,
    setExpandedIndex,
    allowToggle: true,
  });
  const [postalCodeLookupIndex, setPostalCodeLookupIndex] = useState<
    number | null
  >(null);
  const [availableCities, setAvailableCities] = useState<
    Record<number, string[]>
  >({});

  const postalCodeLookupQueuedRef = useRef<Set<number>>(new Set());

  const handleVinLookupResult = useCallback(
    (vehicleIndex: number, result: VinLookupResult, vin: string) => {
      if (result.status === 'pending') {
        const updated = [...vehicles];
        updated[vehicleIndex] = {
          ...updated[vehicleIndex],
          vin_lookup_result: { vin, status: 'pending' },
        };
        setVehicles(updated);
        onUpdate(updated);
        return;
      }

      if (result.status === 'found') {
        const updated = [...vehicles];
        const vehicle = updated[vehicleIndex];

        const vinLookupResult: VinLookupResult = {
          vin,
          status: 'found',
          year: result.year,
          make: result.make,
          model: result.model,
          fuel_type: result.fuel_type,
          vicc_code: result.vicc_code,
          vehicle_ref: result.vehicle_ref,
        };

        updated[vehicleIndex] = {
          ...vehicle,
          vehicle_model_year: result.year
            ? parseInt(result.year, 10)
            : vehicle.vehicle_model_year,
          make: result.make || vehicle.make,
          model: result.model || vehicle.model,
          fuel_type: result.fuel_type || vehicle.fuel_type,
          vicc_code: result.vicc_code || vehicle.vicc_code,
          vehicle_ref: result.vehicle_ref || vehicle.vehicle_ref,
          vin_lookup_result: vinLookupResult,
        };

        setVehicles(updated);
        onUpdate(updated);
        toast.success('Vehicle info populated');
      } else if (result.status === 'multiple_results') {
        const updated = [...vehicles];
        const vehicle = updated[vehicleIndex];

        const vinLookupResult: VinLookupResult = {
          vin,
          status: 'multiple_results',
          year: result.year,
          make: result.make,
          all_results: result.all_results,
        };

        updated[vehicleIndex] = {
          ...vehicle,
          vehicle_model_year: result.year
            ? parseInt(result.year, 10)
            : vehicle.vehicle_model_year,
          make: result.make || vehicle.make,
          vin_lookup_result: vinLookupResult,
        };

        setVehicles(updated);
        onUpdate(updated);
        toast.info('Multiple models found - please select one');
      } else {
        setVinErrorVin(vin);
        setVinErrorMessage(result.error || 'No vehicle found for this VIN');
        onVinErrorOpen();
      }
    },
    [vehicles, onUpdate, toast, onVinErrorOpen],
  );

  const handleViccLookupResult = useCallback(
    (
      vehicleIndex: number,
      result: {
        found: boolean;
        make?: string;
        model?: string;
        fuel_type?: string;
        available_years?: string[];
      },
    ) => {
      if (result.found) {
        const updated = [...vehicles];
        const vehicle = updated[vehicleIndex];

        const autoSelectYear =
          result.available_years?.length === 1
            ? parseInt(result.available_years[0], 10)
            : null;

        updated[vehicleIndex] = {
          ...vehicle,
          make: result.make || vehicle.make,
          model: result.model || vehicle.model,
          fuel_type: result.fuel_type || vehicle.fuel_type,
          vehicle_model_year: autoSelectYear || vehicle.vehicle_model_year,
          vicc_available_years: result.available_years,
        };

        setVehicles(updated);
        onUpdate(updated);

        const fields = ['Make', 'Model'];
        if (result.fuel_type) fields.push('Fuel Type');
        if (autoSelectYear) fields.push('Year');
        toast.success(`${fields.join('/')} populated from VICC`);
      }
    },
    [vehicles, onUpdate, toast],
  );

  const handleModelSelect = useCallback(
    (vehicleIndex: number, viccCode: string) => {
      const vehicle = vehicles[vehicleIndex];
      const allResults = vehicle?.vin_lookup_result?.all_results;
      if (!allResults) return;

      const selected = allResults.find((v) => v.vicc_code === viccCode);
      if (!selected) return;

      const updated = [...vehicles];
      updated[vehicleIndex] = {
        ...updated[vehicleIndex],
        model: selected.model,
        vicc_code: selected.vicc_code,
        fuel_type: selected.fuel_type,
        vehicle_ref: selected.vehicle_ref,
        vin_lookup_result: {
          ...updated[vehicleIndex].vin_lookup_result,
          model: selected.model,
          vicc_code: selected.vicc_code,
          fuel_type: selected.fuel_type,
          vehicle_ref: selected.vehicle_ref,
        },
      };

      setVehicles(updated);
      onUpdate(updated);
    },
    [vehicles, onUpdate],
  );

  const handleManualMakeSelect = useCallback(
    (vehicleIndex: number, manufacturerRef: string, make: string) => {
      const updated = [...vehicles];
      updated[vehicleIndex] = {
        ...updated[vehicleIndex],
        make,
        manufacturer_ref: manufacturerRef,
        model: '',
        model_ref: '',
        vehicle_model_year: null,
        vehicle_ref: '',
        fuel_type: '',
        vicc_code: '',
      };
      setVehicles(updated);
      onUpdate(updated);
    },
    [vehicles, onUpdate],
  );

  const handleManualModelSelect = useCallback(
    (vehicleIndex: number, modelRef: string, model: string) => {
      const updated = [...vehicles];
      updated[vehicleIndex] = {
        ...updated[vehicleIndex],
        model,
        model_ref: modelRef,
        vehicle_model_year: null,
        vehicle_ref: '',
        fuel_type: '',
      };
      setVehicles(updated);
      onUpdate(updated);
    },
    [vehicles, onUpdate],
  );

  const handleManualYearSelect = useCallback(
    (
      vehicleIndex: number,
      year: number,
      vehicleRef: string,
      fuelType: string,
    ) => {
      const updated = [...vehicles];
      updated[vehicleIndex] = {
        ...updated[vehicleIndex],
        vehicle_model_year: year,
        vehicle_ref: vehicleRef,
        fuel_type: fuelType,
      };
      setVehicles(updated);
      onUpdate(updated);
    },
    [vehicles, onUpdate],
  );

  const postalCodeLookupMutation = useMutation({
    mutationFn: apiPostalCodeLookup,
    onSuccess: (result) => {
      if (postalCodeLookupIndex === null) return;

      if (result.found && result.cities && result.cities.length > 0) {
        const vehicleIndex = postalCodeLookupIndex;
        const updated = [...vehicles];
        const vehicle = updated[vehicleIndex];

        if (result.cities.length === 1) {
          updated[vehicleIndex] = {
            ...vehicle,
            garaging: {
              ...vehicle.garaging,
              city: result.cities[0],
            },
          };
          setAvailableCities((prev) => ({ ...prev, [vehicleIndex]: [] }));
        } else {
          setAvailableCities((prev) => ({
            ...prev,
            [vehicleIndex]: result.cities,
          }));
          if (!vehicle.garaging.city) {
            updated[vehicleIndex] = {
              ...vehicle,
              garaging: {
                ...vehicle.garaging,
                city: result.cities[0],
              },
            };
          }
        }

        setVehicles(updated);
        onUpdate(updated);
      }

      setPostalCodeLookupIndex(null);
    },
    onError: () => {
      setPostalCodeLookupIndex(null);
    },
  });

  const handlePostalCodeLookup = (index: number, postalCode: string) => {
    const normalized = postalCode.replace(/\s/g, '');
    if (normalized.length < 3) return;

    setPostalCodeLookupIndex(index);
    postalCodeLookupMutation.mutate(postalCode);
  };

  const handleOpenMap = (index: number) => {
    setMapVehicleIndex(index);
    onMapOpen();
  };

  const handleMapSelect = (
    streetNumber: string,
    streetName: string,
    latitude: number,
    longitude: number,
    postalCode: string,
    city: string,
  ) => {
    if (mapVehicleIndex === null) return;

    const updated = [...vehicles];
    updated[mapVehicleIndex] = {
      ...updated[mapVehicleIndex],
      garaging: {
        ...updated[mapVehicleIndex].garaging,
        street_number: streetNumber,
        street_name: streetName,
        postal_code: postalCode,
        city,
        latitude,
        longitude,
      },
    };

    setVehicles(updated);
    onUpdate(updated);
    setMapVehicleIndex(null);
  };

  useEffect(() => {
    if (data.vehicles && data.vehicles.length > 0) {
      setVehicles((currentVehicles) => {
        return data.vehicles.map((dataVehicle) => {
          const localVehicle = currentVehicles.find(
            (v) => v.id === dataVehicle.id,
          );
          if (
            localVehicle?.vin_lookup_result &&
            !dataVehicle.vin_lookup_result
          ) {
            return {
              ...dataVehicle,
              vin_lookup_result: localVehicle.vin_lookup_result,
            };
          }
          return dataVehicle;
        });
      });

      data.vehicles.forEach((vehicle, index) => {
        const postalCode = vehicle.garaging?.postal_code || '';
        const city = vehicle.garaging?.city || '';
        const normalized = postalCode.replace(/\s/g, '');
        const vehicleKey = vehicle.id;

        if (
          normalized.length >= 3 &&
          !city.trim() &&
          !postalCodeLookupQueuedRef.current.has(vehicleKey)
        ) {
          postalCodeLookupQueuedRef.current.add(vehicleKey);
          handlePostalCodeLookup(index, postalCode);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.vehicles]);

  const handleVehicleChange = (
    index: number,
    field: string,
    value: any,
    nestedField?: string,
  ) => {
    const updated = [...vehicles];
    if (nestedField) {
      updated[index] = {
        ...updated[index],
        [field]: {
          ...(updated[index][field as keyof Vehicle] as object),
          [nestedField]: value,
        },
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    if (field === 'garaging' && nestedField === 'postal_code') {
      updated[index] = {
        ...updated[index],
        garaging: {
          ...updated[index].garaging,
          city: '',
          street_number: '',
          street_name: '',
          latitude: null,
          longitude: null,
        },
      };
      setAvailableCities((prev) => ({ ...prev, [index]: [] }));
    } else if (field === 'garaging' && nestedField === 'city') {
      updated[index] = {
        ...updated[index],
        garaging: {
          ...updated[index].garaging,
          latitude: null,
          longitude: null,
        },
      };
    }

    if (field === 'vehicle_use') {
      const newUseLabel = getLabel(value, 'vehicle_use').toLowerCase();
      if (newUseLabel === 'pleasure') {
        updated[index] = {
          ...updated[index],
          distance: {
            ...updated[index].distance,
            one_way_km: null,
          },
        };
      }
    }

    if (field === 'purchase_info' && nestedField === 'condition') {
      const vin = (updated[index].vin || '').toUpperCase();
      const dashVins = (data.dash_vehicle_vins || []).map((v) =>
        v.toUpperCase(),
      );
      const isVehicleOnDash = vin && dashVins.includes(vin);

      const currentConditionLabel = getLabel(
        vehicles[index].purchase_info?.condition || '',
        'condition_when_purchased_leased',
      ).toLowerCase();
      const newConditionLabel = getLabel(
        value,
        'condition_when_purchased_leased',
      ).toLowerCase();

      const wasNew = currentConditionLabel === 'new';
      const wasDemo = currentConditionLabel.startsWith('demo');
      const isNew = newConditionLabel === 'new';
      const isDemo = newConditionLabel.startsWith('demo');
      const kmAtPurchase = updated[index].purchase_info.km_at_purchase ?? 0;

      const wasEligible =
        !isVehicleOnDash && (wasNew || (wasDemo && kmAtPurchase < 5000));
      const isNowEligible =
        !isVehicleOnDash && (isNew || (isDemo && kmAtPurchase < 5000));

      if (!wasEligible && isNowEligible) {
        updated[index] = {
          ...updated[index],
          coverages: {
            ...updated[index].coverages,
            opcf_43_enabled: true,
            opcf_43_indemnity_period_months: 60,
          },
        };
      } else if (wasEligible && !isNowEligible) {
        updated[index] = {
          ...updated[index],
          coverages: {
            ...updated[index].coverages,
            opcf_43_enabled: false,
            opcf_43_indemnity_period_months: null,
          },
        };
      }
    }

    if (field === 'purchase_info' && nestedField === 'km_at_purchase') {
      const vin = (updated[index].vin || '').toUpperCase();
      const dashVins = (data.dash_vehicle_vins || []).map((v) =>
        v.toUpperCase(),
      );
      const isVehicleOnDash = vin && dashVins.includes(vin);

      const conditionLabel = getLabel(
        updated[index].purchase_info.condition,
        'condition_when_purchased_leased',
      ).toLowerCase();
      const isDemo = conditionLabel.startsWith('demo');

      if (isDemo && !isVehicleOnDash) {
        const currentKm = vehicles[index].purchase_info?.km_at_purchase ?? 0;
        const newKm = typeof value === 'number' ? value : 0;
        const wasEligible = currentKm < 5000;
        const isNowEligible = newKm < 5000;

        if (!wasEligible && isNowEligible) {
          updated[index] = {
            ...updated[index],
            coverages: {
              ...updated[index].coverages,
              opcf_43_enabled: true,
              opcf_43_indemnity_period_months: 60,
            },
          };
        } else if (wasEligible && !isNowEligible) {
          updated[index] = {
            ...updated[index],
            coverages: {
              ...updated[index].coverages,
              opcf_43_enabled: false,
              opcf_43_indemnity_period_months: null,
            },
          };
        }
      }
    }

    if (field === 'vehicle_ownership') {
      const currentOwnershipLabel = getLabel(
        vehicles[index].vehicle_ownership || '',
        'vehicle_ownership',
      ).toLowerCase();
      const newOwnershipLabel = getLabel(
        value || '',
        'vehicle_ownership',
      ).toLowerCase();

      let coverageUpdates: Record<string, boolean> = {};

      if (
        currentOwnershipLabel !== 'financed' &&
        newOwnershipLabel === 'financed'
      ) {
        coverageUpdates.lienholder_endorsement_enabled = true;
      } else if (
        currentOwnershipLabel === 'financed' &&
        newOwnershipLabel !== 'financed'
      ) {
        coverageUpdates.lienholder_endorsement_enabled = false;
      }

      if (
        currentOwnershipLabel !== 'leased' &&
        newOwnershipLabel === 'leased'
      ) {
        coverageUpdates.lessor_endorsement_enabled = true;
      } else if (
        currentOwnershipLabel === 'leased' &&
        newOwnershipLabel !== 'leased'
      ) {
        coverageUpdates.lessor_endorsement_enabled = false;
      }

      if (Object.keys(coverageUpdates).length > 0) {
        updated[index] = {
          ...updated[index],
          coverages: {
            ...updated[index].coverages,
            ...coverageUpdates,
          },
        };
      }
    }

    setVehicles(updated);
    onUpdate(updated);
  };

  const handleRegisteredOwnerChange = (
    vehicleIndex: number,
    ownerIndex: number,
    field: 'first_name' | 'last_name' | 'business_name',
    value: string,
  ) => {
    const updated = [...vehicles];
    const owners = [...updated[vehicleIndex].registered_owners];
    owners[ownerIndex] = { ...owners[ownerIndex], [field]: value };
    updated[vehicleIndex] = {
      ...updated[vehicleIndex],
      registered_owners: owners,
    };
    setVehicles(updated);
    onUpdate(updated);
  };

  const addPersonOwner = useCallback(
    (vehicleIndex: number) => {
      const updated = [...vehicles];
      updated[vehicleIndex] = {
        ...updated[vehicleIndex],
        registered_owners: [
          ...updated[vehicleIndex].registered_owners,
          { first_name: '', last_name: '' },
        ],
      };
      setVehicles(updated);
      onUpdate(updated);
    },
    [vehicles, onUpdate],
  );

  const addBusinessOwner = useCallback(
    (vehicleIndex: number) => {
      const updated = [...vehicles];
      updated[vehicleIndex] = {
        ...updated[vehicleIndex],
        registered_owners: [
          ...updated[vehicleIndex].registered_owners,
          {
            first_name: '',
            last_name: '',
            business_name: '',
            is_business: true,
          },
        ],
      };
      setVehicles(updated);
      onUpdate(updated);
    },
    [vehicles, onUpdate],
  );

  const handleAddRegisteredOwner = (vehicleIndex: number) => {
    if (data.business_name_vehicles) {
      setOwnerTypeModalIndex(vehicleIndex);
      onOwnerTypeOpen();
    } else {
      addPersonOwner(vehicleIndex);
    }
  };

  const handleOwnerTypeSelect = (type: 'person' | 'business') => {
    if (ownerTypeModalIndex === null) return;
    if (type === 'person') {
      addPersonOwner(ownerTypeModalIndex);
    } else {
      addBusinessOwner(ownerTypeModalIndex);
    }
    setOwnerTypeModalIndex(null);
    onOwnerTypeClose();
  };

  const handleRemoveRegisteredOwner = (
    vehicleIndex: number,
    ownerIndex: number,
  ) => {
    const updated = [...vehicles];
    const owners = updated[vehicleIndex].registered_owners.filter(
      (_, i) => i !== ownerIndex,
    );
    updated[vehicleIndex] = {
      ...updated[vehicleIndex],
      registered_owners: owners,
    };
    setVehicles(updated);
    onUpdate(updated);
  };

  const applyDefaultCoverages = useCallback(
    (vehicle: Vehicle): Vehicle => {
      const liabilityCode = getCode('2,000,000', 'liability_limit_cad');
      const dcpdCode = getCode('0', 'dcpd_deductible_cad');
      const allPerilsCode = getCode('1,000', 'all_perils_deductible_cad');

      return {
        ...vehicle,
        coverages: {
          ...vehicle.coverages,
          liability_limit_cad:
            liabilityCode || vehicle.coverages.liability_limit_cad,
          dcpd_deductible_cad:
            dcpdCode || vehicle.coverages.dcpd_deductible_cad,
          all_perils_deductible_cad:
            allPerilsCode || vehicle.coverages.all_perils_deductible_cad,
        },
      };
    },
    [getCode],
  );

  const handleAddVehicle = () => {
    const maxId = Math.max(...vehicles.map((v) => v.id), 0);
    const newVehicle = applyDefaultCoverages(createEmptyVehicle(maxId + 1));

    if (data.business_name_vehicles && data.business_name) {
      const businessOwner = {
        first_name: '',
        last_name: '',
        business_name: data.business_name,
        is_business: true,
      };
      newVehicle.registered_owners = [businessOwner];
    }

    const updated = [...vehicles, newVehicle];
    setVehicles(updated);
    setExpandedIndex(updated.length - 1);
    onUpdate(updated);
  };

  const handleDeleteClick = (index: number) => {
    setVehicleToDelete(index);
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    if (vehicleToDelete === null) return;

    const updated = vehicles.filter((_, i) => i !== vehicleToDelete);
    setVehicles(updated);
    if (updated.length === 0) {
      setExpandedIndex(-1);
    } else if (expandedIndex >= updated.length) {
      setExpandedIndex(updated.length - 1);
    }
    onUpdate(updated);
    onDeleteClose();
    setVehicleToDelete(null);
  };

  const getVehicleTitle = (vehicle: Vehicle, _index: number) => {
    const label = [vehicle.vehicle_model_year, vehicle.make, vehicle.model]
      .filter(Boolean)
      .join(' ');
    if (label) return label;
    if (vehicle.vin) return `VIN: ${vehicle.vin}`;
    return '';
  };

  const getVehicleSubtitle = (vehicle: Vehicle): string | undefined => {
    if (
      vehicle.vehicle_model_year &&
      vehicle.make &&
      vehicle.model &&
      vehicle.vin
    ) {
      return `VIN: ${vehicle.vin}`;
    }
    return undefined;
  };

  const renderVehicleForm = (vehicle: Vehicle, index: number) => {
    return (
      <ValidationEntityProvider
        entity={vehicle as unknown as Record<string, unknown>}
      >
        <Box>
          <VehicleCard
            vehicle={vehicle}
            index={index}
            quoteId={quoteId}
            isUpdating={isUpdating}
            onVehicleChange={handleVehicleChange}
            onVinLookupResult={handleVinLookupResult}
            onViccLookupResult={handleViccLookupResult}
            onModelSelect={handleModelSelect}
            onManualMakeSelect={handleManualMakeSelect}
            onManualModelSelect={handleManualModelSelect}
            onManualYearSelect={handleManualYearSelect}
            onAddRegisteredOwner={handleAddRegisteredOwner}
            onRegisteredOwnerChange={handleRegisteredOwnerChange}
            onRemoveRegisteredOwner={handleRemoveRegisteredOwner}
          />

          <Grid
            templateColumns={{
              base: '1fr',
              lg: 'repeat(5, 1fr)',
              xl: 'repeat(3, 1fr)',
            }}
            gap={4}
          >
            <GridItem colSpan={{ base: 1, lg: 3, xl: 2 }}>
              <VehicleGaraging
                vehicle={vehicle}
                index={index}
                onVehicleChange={handleVehicleChange}
                postalCodeLookupIndex={postalCodeLookupIndex}
                availableCities={availableCities[index] || []}
                onPostalCodeChange={handlePostalCodeLookup}
                onOpenMap={handleOpenMap}
                contactProvinceMismatch={
                  data.contact_province != null &&
                  data.province != null &&
                  data.contact_province !== data.province
                }
              />

              <VehicleUseDistanceValue
                vehicle={vehicle}
                index={index}
                onVehicleChange={handleVehicleChange}
              />

              <VehiclePurchaseInfo
                vehicle={vehicle}
                index={index}
                onVehicleChange={handleVehicleChange}
              />
            </GridItem>

            <GridItem colSpan={{ base: 1, lg: 2, xl: 1 }}>
              <VehicleCoverages
                vehicle={vehicle}
                index={index}
                onVehicleChange={handleVehicleChange}
                quoteId={data.id}
                provinceCode={data.province}
              />
            </GridItem>
          </Grid>
        </Box>
      </ValidationEntityProvider>
    );
  };

  return (
    <Box>
      <Accordion {...accordionProps}>
        {vehicles.map((vehicle, index) => (
          <CollapsibleItemCard
            key={vehicle.id}
            title={getVehicleTitle(vehicle, index)}
            subtitle={getVehicleSubtitle(vehicle)}
            onDelete={() => handleDeleteClick(index)}
          >
            {renderVehicleForm(vehicle, index)}
          </CollapsibleItemCard>
        ))}
      </Accordion>

      <AddItemButton label="Vehicle" onClick={handleAddVehicle} />

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleConfirmDelete}
        entityName="Vehicle"
        itemTitle={
          vehicleToDelete !== null
            ? getVehicleTitle(vehicles[vehicleToDelete], vehicleToDelete)
            : 'this vehicle'
        }
      />

      <Modal isOpen={isVinErrorOpen} onClose={onVinErrorClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>VIN Lookup Failed</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontWeight="medium" mb={2}>
              VIN: {vinErrorVin}
            </Text>
            <Text>{vinErrorMessage}</Text>
            <Text mt={3} fontSize="sm" color="gray.500">
              You can enter the vehicle details manually.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand" onClick={onVinErrorClose}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOwnerTypeOpen} onClose={onOwnerTypeClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Registered Owner</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Is this registered owner a person or a business?</Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              variant="outline"
              onClick={() => handleOwnerTypeSelect('person')}
            >
              Person
            </Button>
            <Button
              colorScheme="brand"
              onClick={() => handleOwnerTypeSelect('business')}
            >
              Business
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {mapVehicleIndex !== null && (
        <ViewGoogleMapsModal
          isOpen={isMapOpen}
          onClose={() => {
            onMapClose();
            setMapVehicleIndex(null);
          }}
          onSelect={handleMapSelect}
          postalCode={vehicles[mapVehicleIndex].garaging.postal_code}
          city={vehicles[mapVehicleIndex].garaging.city}
          initialStreetNumber={vehicles[mapVehicleIndex].garaging.street_number}
          initialStreetName={vehicles[mapVehicleIndex].garaging.street_name}
          initialLatitude={vehicles[mapVehicleIndex].garaging.latitude}
          initialLongitude={vehicles[mapVehicleIndex].garaging.longitude}
        />
      )}
    </Box>
  );
};

export default VehicleStep;
